import type { FC } from 'react';
import React, { useCallback, useContext } from 'react';
import { cssMap } from '@compiled/react';

import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Inline, Stack, Box, xcss } from '@atlaskit/primitives';

import { AISnippetWithRelayEnvironment } from '@atlassian/ai-snippet';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { FeatureCode } from '@confluence/feature-codes/entry-points/FeatureCodeModel';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { useIsEligibleRovoAwareness } from '@confluence/rovo-awareness/entry-points/useIsEligibleRovoAwareness';
import { expValEquals } from '@confluence/feature-experiments';

import { PageCardContext } from '../PageCardContext';
import {
	AutomationDiscoveryMessage,
	ContentIcon,
	LastAction,
	PageCardContainer,
	PageLink,
	SocialProof,
	Subtitle,
	Title,
	AudioPlaylistButton,
} from '../Items';

const styles = xcss({
	width: '400px',
});
const headerStyle = xcss({
	width: '100%',
	overflowWrap: 'break-word',
	wordBreak: 'break-all',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});
const headerIconStyle = xcss({
	display: 'flex',
	justifyContent: 'space-between',
	alignSelf: 'flex-start',
});

const titleStyles = xcss({
	font: token('font.heading.medium'),
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	overflowWrap: 'break-word',
	display: '-webkit-box',
	WebkitLineClamp: '2',
	WebkitBoxOrient: 'vertical',
});

const aiSnippetStyles = cssMap({
	container: {
		minHeight: '96px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		pre: {
			// We need this so code wraps in the preview, without it the code will overflow the card
			whiteSpace: 'pre-wrap',
		},
	},
});

type PopupViewWithAISnippetProps = {
	shouldUseAutomationDiscovery?: boolean; // this should probably go into PageTreeContext, however since this is the only usage, it's ok to leave it here for now
	error?: boolean;
	snippet?: string;
	isEndOfPage?: boolean;
	contentId: string;
	contentType: string;
	shouldUseAISnippetPlatformComponent?: boolean;
	onLoadComplete?: () => void;
};

export const PopupViewWithAISnippet: FC<PopupViewWithAISnippetProps> = ({
	shouldUseAutomationDiscovery,
	snippet,
	isEndOfPage = false,
	contentId,
	contentType,
	onLoadComplete,
	shouldUseAISnippetPlatformComponent = false,
}) => {
	const { cloudId, userId } = useSessionData();
	const { excerpt } = useContext(PageCardContext);
	const { trackAIResultAction } = useAIEventsInstrumentation();
	const isEligible = useIsEligibleRovoAwareness();
	const shouldDisplayAwareness =
		isEligible && expValEquals('rovo_awareness_experiment', 'cohort', 'test');

	const handlePopupClick = useCallback(() => {
		if (snippet || shouldUseAISnippetPlatformComponent) {
			trackAIResultAction('aiSnippetCardClicked', {
				attributes: {
					source: isEndOfPage ? 'endOfPageRecommendations' : 'pageTree',
				},
			});
		}
	}, [trackAIResultAction, isEndOfPage, snippet, shouldUseAISnippetPlatformComponent]);

	return (
		<PageCardContainer isHoverPageCard onClick={handlePopupClick}>
			<PageLink
				shouldHideFolder
				featureAttributionCode={isEndOfPage ? FeatureCode.EOP_REC_CARD : FeatureCode.PAGE_TREE_CARD}
			/>
			<Box padding="space.200" xcss={styles} testId="page-card-popup-view">
				<Stack alignBlock="start" alignInline="start" grow="fill" space="space.200">
					<Inline
						alignBlock="center"
						alignInline="start"
						shouldWrap={false}
						grow="fill"
						space="space.100"
					>
						<Box xcss={headerIconStyle}>
							<ContentIcon />
						</Box>
						<Stack xcss={headerStyle}>
							<Box xcss={titleStyles}>
								<Title />
							</Box>
							<Subtitle />
						</Stack>
					</Inline>
					{excerpt && (
						<Box xcss={aiSnippetStyles.container}>
							<AISnippetWithRelayEnvironment
								contentId={contentId}
								contentType={contentType}
								cloudId={cloudId}
								source={isEndOfPage ? 'endOfPageRecommendations' : 'pageTree'}
								onLoadComplete={onLoadComplete}
								fallbackText={excerpt}
								showAwareness={shouldDisplayAwareness}
								from={isEndOfPage ? FeatureCode.EOP_REC_CARD : FeatureCode.PAGE_TREE_CARD}
								userId={userId ?? undefined}
							/>
						</Box>
					)}
					<Inline
						alignBlock="center"
						spread="space-between"
						shouldWrap={false}
						grow="fill"
						space="space.200"
					>
						<LastAction />
						<SocialProof />
						<AudioPlaylistButton />
					</Inline>
				</Stack>
			</Box>
			<AutomationDiscoveryMessage shouldUseAutomationDiscovery={shouldUseAutomationDiscovery} />
		</PageCardContainer>
	);
};
